import React, { useState, useEffect } from 'react';
import { api } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DateRangeFilter } from '../components/ui/DateRangeFilter';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Receipt, AlertTriangle, 
  Clock, CheckCircle, DollarSign, RefreshCw, Tag, Palette, Calendar
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { fr } from 'date-fns/locale';
import { formatCurrency } from '../utils/formatters';

const FREQUENCY_OPTIONS = [
  { value: 'MONTHLY', label: 'Mensuelle' },
  { value: 'QUARTERLY', label: 'Trimestrielle' },
  { value: 'YEARLY', label: 'Annuelle' }
];

const COLOR_OPTIONS = [
  '#EF4444', '#F97316', '#F59E0B', '#84CC16', '#22C55E', 
  '#14B8A6', '#06B6D4', '#3B82F6', '#6366F1', '#8B5CF6', 
  '#A855F7', '#EC4899', '#6B7280'
];

export default function ChargesPage() {
  const [recurringCharges, setRecurringCharges] = useState([]);
  const [payments, setPayments] = useState([]);
  const [chargesDue, setChargesDue] = useState({ due: [], overdue: [] });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('recurring');
  
  // Dialogs
  const [showChargeDialog, setShowChargeDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [showOneTimeDialog, setShowOneTimeDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [showEditPaymentDialog, setShowEditPaymentDialog] = useState(false);
  
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // Form data for recurring charge
  const [chargeForm, setChargeForm] = useState({
    name: '',
    amount: '',
    frequency: 'MONTHLY',
    day_of_month: '1',
    category_id: '',
    notes: '',
    next_due: format(new Date(), 'yyyy-MM-dd')
  });
  
  // Form data for payment
  const [payForm, setPayForm] = useState({
    recurring_charge_id: '',
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    payment_method: 'TRANSFER', // TRANSFER, CASH, CARD, CHECK
    notes: ''
  });
  
  // Form data for one-time charge
  const [oneTimeForm, setOneTimeForm] = useState({
    name: '',
    amount: '',
    payment_date: format(new Date(), 'yyyy-MM-dd'),
    category_id: '',
    payment_method: 'TRANSFER', // TRANSFER, CASH, CARD, CHECK
    notes: ''
  });
  
  // Form data for category
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    color: '#6B7280'
  });
  
  // Form data for editing payment
  const [editPaymentForm, setEditPaymentForm] = useState({
    amount: '',
    payment_date: '',
    notes: ''
  });
  
  // Date filters
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  useEffect(() => {
    fetchAll();
  }, [dateFrom, dateTo, categoryFilter]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchRecurringCharges(),
      fetchPayments(),
      fetchChargesDue(),
      fetchCategories()
    ]);
    setLoading(false);
  };

  const fetchRecurringCharges = async () => {
    try {
      const response = await api.get('/charges/recurring');
      setRecurringCharges(response.data);
    } catch (error) {
      console.error('Fetch recurring charges error:', error);
    }
  };

  const fetchPayments = async () => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (categoryFilter && categoryFilter !== 'all') params.category_id = categoryFilter;
      const response = await api.get('/charges/payments', { params });
      setPayments(response.data);
    } catch (error) {
      console.error('Fetch payments error:', error);
    }
  };

  const fetchChargesDue = async () => {
    try {
      const response = await api.get('/charges/due');
      setChargesDue(response.data);
    } catch (error) {
      console.error('Fetch charges due error:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get('/charges/categories');
      setCategories(response.data);
    } catch (error) {
      console.error('Fetch categories error:', error);
    }
  };

  // CRUD for recurring charges
  const handleCreateCharge = async (e) => {
    e.preventDefault();
    try {
      if (selectedCharge) {
        await api.put(`/charges/recurring/${selectedCharge.id}`, chargeForm);
        toast.success('Charge mise à jour');
      } else {
        await api.post('/charges/recurring', chargeForm);
        toast.success('Charge créée');
      }
      setShowChargeDialog(false);
      resetChargeForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  const handleDeleteCharge = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette charge ?')) return;
    try {
      await api.delete(`/charges/recurring/${id}`);
      toast.success('Charge désactivée');
      fetchAll();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handlePayCharge = async (e) => {
    e.preventDefault();
    try {
      await api.post('/charges/pay', payForm);
      toast.success('Paiement enregistré');
      setShowPayDialog(false);
      resetPayForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors du paiement');
    }
  };

  const handleCreateOneTime = async (e) => {
    e.preventDefault();
    try {
      await api.post('/charges/one-time', oneTimeForm);
      toast.success('Charge ponctuelle créée');
      setShowOneTimeDialog(false);
      resetOneTimeForm();
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la création');
    }
  };

  // CRUD for categories
  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      if (selectedCategory) {
        await api.put(`/charges/categories/${selectedCategory.id}`, categoryForm);
        toast.success('Catégorie mise à jour');
      } else {
        await api.post('/charges/categories', categoryForm);
        toast.success('Catégorie créée');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer cette catégorie ?')) return;
    try {
      await api.delete(`/charges/categories/${id}`);
      toast.success('Catégorie supprimée');
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // CRUD for payments
  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/charges/payments/${selectedPayment.id}`, editPaymentForm);
      toast.success('Paiement mis à jour');
      setShowEditPaymentDialog(false);
      setSelectedPayment(null);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la mise à jour');
    }
  };

  const handleDeletePayment = async (id) => {
    if (!window.confirm('Voulez-vous vraiment supprimer ce paiement ?')) return;
    try {
      await api.delete(`/charges/payments/${id}`);
      toast.success('Paiement supprimé');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erreur lors de la suppression');
    }
  };

  // Reset forms
  const resetChargeForm = () => {
    setChargeForm({
      name: '',
      amount: '',
      frequency: 'MONTHLY',
      day_of_month: '1',
      category_id: '',
      notes: '',
      next_due: format(new Date(), 'yyyy-MM-dd')
    });
    setSelectedCharge(null);
  };

  const resetPayForm = () => {
    setPayForm({
      recurring_charge_id: '',
      amount: '',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      payment_method: 'TRANSFER',
      notes: ''
    });
  };

  const resetOneTimeForm = () => {
    setOneTimeForm({
      name: '',
      amount: '',
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      category_id: '',
      payment_method: 'TRANSFER',
      notes: ''
    });
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', color: '#6B7280' });
    setSelectedCategory(null);
  };

  const openEditDialog = (charge) => {
    setSelectedCharge(charge);
    setChargeForm({
      name: charge.name,
      amount: charge.amount.toString(),
      frequency: charge.frequency,
      day_of_month: charge.day_of_month?.toString() || '1',
      category_id: charge.category_id?.toString() || '',
      notes: charge.notes || '',
      next_due: charge.next_due || format(new Date(), 'yyyy-MM-dd')
    });
    setShowChargeDialog(true);
  };

  const openPayDialog = (charge) => {
    setPayForm({
      recurring_charge_id: charge.id.toString(),
      amount: charge.amount.toString(),
      payment_date: format(new Date(), 'yyyy-MM-dd'),
      payment_method: 'TRANSFER',
      notes: ''
    });
    setShowPayDialog(true);
  };

  const openEditCategoryDialog = (cat) => {
    setSelectedCategory(cat);
    setCategoryForm({ name: cat.name, color: cat.color });
    setShowCategoryDialog(true);
  };

  const openEditPaymentDialog = (payment) => {
    setSelectedPayment(payment);
    setEditPaymentForm({
      amount: payment.amount.toString(),
      payment_date: payment.payment_date,
      notes: payment.notes || ''
    });
    setShowEditPaymentDialog(true);
  };

  // Calculate summary
  const totalRecurring = recurringCharges
    .filter(c => c.active)
    .reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
  
  const totalPaid = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="charges-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Gestion des Charges</h1>
          <p className="text-muted-foreground mt-1">Charges récurrentes et ponctuelles</p>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => { resetChargeForm(); setShowChargeDialog(true); }} data-testid="add-recurring-charge-btn">
            <Plus className="h-4 w-4 mr-2" />
            Charge récurrente
          </Button>
          <Button variant="outline" onClick={() => { resetOneTimeForm(); setShowOneTimeDialog(true); }} data-testid="add-one-time-charge-btn">
            <Plus className="h-4 w-4 mr-2" />
            Charge ponctuelle
          </Button>
          <Button variant="ghost" size="icon" onClick={fetchAll} data-testid="refresh-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Alerts for due charges */}
      {(chargesDue.overdue?.length > 0 || chargesDue.due?.length > 0) && (
        <div className="space-y-2">
          {chargesDue.overdue?.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3" data-testid="overdue-alert">
              <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-red-800">Charges en retard ({chargesDue.overdue.length})</p>
                <div className="text-sm text-red-700 mt-1 space-y-1">
                  {chargesDue.overdue.map(charge => (
                    <div key={charge.id} className="flex items-center justify-between gap-4">
                      <span>{charge.name} - {formatCurrency(charge.amount)}</span>
                      <Button size="sm" variant="destructive" onClick={() => openPayDialog(charge)}>
                        Payer maintenant
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          
          {chargesDue.due?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-testid="due-alert">
              <Clock className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Charges à payer ce mois ({chargesDue.due.length})</p>
                <div className="text-sm text-amber-700 mt-1 space-y-1">
                  {chargesDue.due.map(charge => {
                    const daysUntil = differenceInDays(new Date(charge.next_due), new Date());
                    return (
                      <div key={charge.id} className="flex items-center justify-between gap-4">
                        <span>
                          {charge.name} - {formatCurrency(charge.amount)} 
                          <span className="text-amber-600 ml-2">(dans {daysUntil} jours)</span>
                        </span>
                        <Button size="sm" variant="outline" onClick={() => openPayDialog(charge)}>
                          Payer
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Charges récurrentes</p>
                <p className="text-2xl font-bold text-blue-800">{recurringCharges.filter(c => c.active).length}</p>
              </div>
              <Receipt className="h-8 w-8 text-blue-500" />
            </div>
            <p className="text-xs mt-1 text-muted-foreground">actives</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Montant mensuel</p>
                <p className="text-2xl font-bold text-orange-800">{formatCurrency(totalRecurring)}</p>
              </div>
              <DollarSign className="h-8 w-8 text-orange-500" />
            </div>
            <p className="text-xs mt-1 text-muted-foreground">estimé</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Total payé</p>
                <p className="text-2xl font-bold text-green-800">{formatCurrency(totalPaid)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <p className="text-xs mt-1 text-muted-foreground">période en cours</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600 font-medium">Catégories</p>
                <p className="text-2xl font-bold text-purple-800">{categories.length}</p>
              </div>
              <Tag className="h-8 w-8 text-purple-500" />
            </div>
            <p className="text-xs mt-1 text-muted-foreground">de charges</p>
          </CardContent>
        </Card>
      </div>

      {/* Date and Category Filter for payments tab */}
      {activeTab === 'payments' && (
        <Card className="p-4">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1">
              <DateRangeFilter
                startDate={dateFrom}
                endDate={dateTo}
                onStartDateChange={setDateFrom}
                onEndDateChange={setDateTo}
                onReset={() => { setDateFrom(''); setDateTo(''); }}
                showQuickFilters={true}
              />
            </div>
            <div className="w-full sm:w-48">
              <Label className="text-xs text-muted-foreground mb-1 block">Catégorie</Label>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger data-testid="payment-category-filter">
                  <SelectValue placeholder="Toutes catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                        {cat.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-lg">
          <TabsTrigger value="recurring" data-testid="tab-recurring">
            <Receipt className="w-4 h-4 mr-2" />
            Charges
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <Calendar className="w-4 h-4 mr-2" />
            Paiements
          </TabsTrigger>
          <TabsTrigger value="categories" data-testid="tab-categories">
            <Tag className="w-4 h-4 mr-2" />
            Catégories
          </TabsTrigger>
        </TabsList>

        {/* Recurring charges tab */}
        <TabsContent value="recurring" className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : recurringCharges.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucune charge récurrente. Cliquez sur "Charge récurrente" pour en créer une.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {recurringCharges.map(charge => (
                <Card key={charge.id} className={`${!charge.active ? 'opacity-50' : ''}`} data-testid={`charge-card-${charge.id}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg">{charge.name}</CardTitle>
                      <Badge variant={charge.active ? 'default' : 'secondary'}>
                        {charge.active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {charge.category && (
                      <Badge variant="outline" style={{ backgroundColor: `${charge.category.color}20`, borderColor: charge.category.color }}>
                        {charge.category.name}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-muted-foreground">Montant</span>
                        <span className="font-bold text-lg">{formatCurrency(charge.amount)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Fréquence</span>
                        <span>{FREQUENCY_OPTIONS.find(f => f.value === charge.frequency)?.label || charge.frequency}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-muted-foreground">Prochaine échéance</span>
                        <span className={`font-medium ${new Date(charge.next_due) < new Date() ? 'text-red-600' : ''}`}>
                          {format(new Date(charge.next_due), 'dd MMM yyyy', { locale: fr })}
                        </span>
                      </div>
                      {charge.last_paid && (
                        <div className="flex justify-between items-center text-sm">
                          <span className="text-muted-foreground">Dernier paiement</span>
                          <span>{format(new Date(charge.last_paid), 'dd MMM yyyy', { locale: fr })}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-4 pt-2 border-t">
                        <Button size="sm" className="flex-1" onClick={() => openPayDialog(charge)} data-testid={`pay-charge-${charge.id}`}>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Payer
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEditDialog(charge)} data-testid={`edit-charge-${charge.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteCharge(charge.id)} data-testid={`delete-charge-${charge.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Payments history tab */}
        <TabsContent value="payments" className="space-y-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Chargement...</div>
          ) : payments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucun paiement enregistré.
            </div>
          ) : (
            <Card>
              <CardContent className="pt-4">
                <div className="space-y-2">
                  {payments.map(payment => (
                    <div key={payment.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg" data-testid={`payment-${payment.id}`}>
                      <div className="flex items-center gap-3">
                        <div className="w-2 h-10 rounded-full" style={{ backgroundColor: payment.category?.color || '#6B7280' }} />
                        <div>
                          <p className="font-medium">
                            {payment.is_one_time 
                              ? payment.one_time_name || 'Charge ponctuelle'
                              : payment.recurringCharge?.name || 'Charge'}
                          </p>
                          <div className="text-xs text-muted-foreground flex items-center gap-2">
                            <span>{format(new Date(payment.payment_date), 'dd MMMM yyyy', { locale: fr })}</span>
                            {payment.is_one_time && <Badge variant="outline" className="text-xs">Ponctuelle</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-bold text-lg">{formatCurrency(payment.amount)}</span>
                        <Button size="sm" variant="ghost" onClick={() => openEditPaymentDialog(payment)} data-testid={`edit-payment-${payment.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeletePayment(payment.id)} data-testid={`delete-payment-${payment.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Categories tab */}
        <TabsContent value="categories" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-medium">Catégories de charges</h3>
            <Button onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }} data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle catégorie
            </Button>
          </div>
          
          {categories.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Aucune catégorie. Cliquez sur "Nouvelle catégorie" pour en créer une.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {categories.map(cat => (
                <Card key={cat.id} data-testid={`category-card-${cat.id}`}>
                  <CardContent className="pt-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div 
                          className="w-8 h-8 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: cat.color }}
                        >
                          <Tag className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-medium">{cat.name}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEditCategoryDialog(cat)} data-testid={`edit-category-${cat.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700" onClick={() => handleDeleteCategory(cat.id)} data-testid={`delete-category-${cat.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialog: Create/Edit Recurring Charge */}
      <Dialog open={showChargeDialog} onOpenChange={setShowChargeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCharge ? 'Modifier la charge' : 'Nouvelle charge récurrente'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateCharge}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de la charge *</Label>
                <Input 
                  value={chargeForm.name}
                  onChange={(e) => setChargeForm({ ...chargeForm, name: e.target.value })}
                  placeholder="Ex: Loyer, Électricité..."
                  required
                  data-testid="charge-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (MAD) *</Label>
                  <Input 
                    type="number"
                    value={chargeForm.amount}
                    onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                    data-testid="charge-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Fréquence *</Label>
                  <Select value={chargeForm.frequency} onValueChange={(v) => setChargeForm({ ...chargeForm, frequency: v })}>
                    <SelectTrigger data-testid="charge-frequency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FREQUENCY_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Jour du mois</Label>
                  <Input 
                    type="number"
                    value={chargeForm.day_of_month}
                    onChange={(e) => setChargeForm({ ...chargeForm, day_of_month: e.target.value })}
                    min="1"
                    max="31"
                    data-testid="charge-day-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Prochaine échéance</Label>
                  <Input 
                    type="date"
                    value={chargeForm.next_due}
                    onChange={(e) => setChargeForm({ ...chargeForm, next_due: e.target.value })}
                    data-testid="charge-next-due-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={chargeForm.category_id} onValueChange={(v) => setChargeForm({ ...chargeForm, category_id: v })}>
                  <SelectTrigger data-testid="charge-category-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={chargeForm.notes}
                  onChange={(e) => setChargeForm({ ...chargeForm, notes: e.target.value })}
                  placeholder="Notes optionnelles..."
                  data-testid="charge-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowChargeDialog(false)}>Annuler</Button>
              <Button type="submit" data-testid="save-charge-btn">{selectedCharge ? 'Modifier' : 'Créer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Pay Charge */}
      <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enregistrer un paiement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handlePayCharge}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Charge</Label>
                <Select 
                  value={payForm.recurring_charge_id} 
                  onValueChange={(v) => {
                    const charge = recurringCharges.find(c => c.id.toString() === v);
                    setPayForm({ 
                      ...payForm, 
                      recurring_charge_id: v,
                      amount: charge?.amount?.toString() || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="pay-charge-select">
                    <SelectValue placeholder="Sélectionner une charge..." />
                  </SelectTrigger>
                  <SelectContent>
                    {recurringCharges.filter(c => c.active).map(charge => (
                      <SelectItem key={charge.id} value={charge.id.toString()}>
                        {charge.name} - {formatCurrency(charge.amount)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (MAD) *</Label>
                  <Input 
                    type="number"
                    value={payForm.amount}
                    onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                    data-testid="pay-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date de paiement *</Label>
                  <Input 
                    type="date"
                    value={payForm.payment_date}
                    onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                    required
                    data-testid="pay-date-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mode de paiement *</Label>
                <Select 
                  value={payForm.payment_method}
                  onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="pay-method-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRANSFER">Virement bancaire</SelectItem>
                    <SelectItem value="CASH">Espèces (caisse)</SelectItem>
                    <SelectItem value="CARD">Carte bancaire</SelectItem>
                    <SelectItem value="CHECK">Chèque</SelectItem>
                  </SelectContent>
                </Select>
                {payForm.payment_method === 'CASH' && (
                  <p className="text-xs text-amber-600">
                    Un mouvement de caisse (sortie) sera créé automatiquement
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={payForm.notes}
                  onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  placeholder="Notes optionnelles..."
                  data-testid="pay-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)}>Annuler</Button>
              <Button type="submit" data-testid="confirm-pay-btn">Enregistrer le paiement</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: One-time Charge */}
      <Dialog open={showOneTimeDialog} onOpenChange={setShowOneTimeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nouvelle charge ponctuelle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateOneTime}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Description *</Label>
                <Input 
                  value={oneTimeForm.name}
                  onChange={(e) => setOneTimeForm({ ...oneTimeForm, name: e.target.value })}
                  placeholder="Ex: Réparation plomberie..."
                  required
                  data-testid="onetime-name-input"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (MAD) *</Label>
                  <Input 
                    type="number"
                    value={oneTimeForm.amount}
                    onChange={(e) => setOneTimeForm({ ...oneTimeForm, amount: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                    data-testid="onetime-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input 
                    type="date"
                    value={oneTimeForm.payment_date}
                    onChange={(e) => setOneTimeForm({ ...oneTimeForm, payment_date: e.target.value })}
                    required
                    data-testid="onetime-date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Catégorie</Label>
                <Select value={oneTimeForm.category_id} onValueChange={(v) => setOneTimeForm({ ...oneTimeForm, category_id: v })}>
                  <SelectTrigger data-testid="onetime-category-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                          {cat.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Mode de paiement *</Label>
                <Select 
                  value={oneTimeForm.payment_method}
                  onValueChange={(v) => setOneTimeForm({ ...oneTimeForm, payment_method: v })}
                >
                  <SelectTrigger data-testid="onetime-method-select">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="TRANSFER">Virement bancaire</SelectItem>
                    <SelectItem value="CASH">Espèces (caisse)</SelectItem>
                    <SelectItem value="CARD">Carte bancaire</SelectItem>
                    <SelectItem value="CHECK">Chèque</SelectItem>
                  </SelectContent>
                </Select>
                {oneTimeForm.payment_method === 'CASH' && (
                  <p className="text-xs text-amber-600">
                    Un mouvement de caisse (sortie) sera créé automatiquement
                  </p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={oneTimeForm.notes}
                  onChange={(e) => setOneTimeForm({ ...oneTimeForm, notes: e.target.value })}
                  placeholder="Notes optionnelles..."
                  data-testid="onetime-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowOneTimeDialog(false)}>Annuler</Button>
              <Button type="submit" data-testid="save-onetime-btn">Créer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Create/Edit Category */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{selectedCategory ? 'Modifier la catégorie' : 'Nouvelle catégorie'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveCategory}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nom de la catégorie *</Label>
                <Input 
                  value={categoryForm.name}
                  onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                  placeholder="Ex: Loyer, Services, Maintenance..."
                  required
                  data-testid="category-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Couleur</Label>
                <div className="flex flex-wrap gap-2">
                  {COLOR_OPTIONS.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-8 h-8 rounded-full border-2 transition-all ${categoryForm.color === color ? 'border-gray-900 scale-110' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                      onClick={() => setCategoryForm({ ...categoryForm, color })}
                    />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCategoryDialog(false)}>Annuler</Button>
              <Button type="submit" data-testid="save-category-btn">{selectedCategory ? 'Modifier' : 'Créer'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog: Edit Payment */}
      <Dialog open={showEditPaymentDialog} onOpenChange={setShowEditPaymentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier le paiement</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePayment}>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Montant (MAD) *</Label>
                  <Input 
                    type="number"
                    value={editPaymentForm.amount}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, amount: e.target.value })}
                    placeholder="0"
                    min="0"
                    step="0.01"
                    required
                    data-testid="edit-payment-amount-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date de paiement *</Label>
                  <Input 
                    type="date"
                    value={editPaymentForm.payment_date}
                    onChange={(e) => setEditPaymentForm({ ...editPaymentForm, payment_date: e.target.value })}
                    required
                    data-testid="edit-payment-date-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea 
                  value={editPaymentForm.notes}
                  onChange={(e) => setEditPaymentForm({ ...editPaymentForm, notes: e.target.value })}
                  placeholder="Notes optionnelles..."
                  data-testid="edit-payment-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditPaymentDialog(false)}>Annuler</Button>
              <Button type="submit" data-testid="save-edit-payment-btn">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
